/**
 * @file ResetPasswordForm.jsx
 * @description This component handles the reset password functionality for users who have forgotten their password.
 * It verifies the password reset code, allows users to enter a new password, and updates their password in backend and firebase.
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useParams } from 'react-router-dom';
import { verifyPasswordResetCode } from 'firebase/auth';
import {
    Dialog,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Stack,
    CircularProgress
} from '@mui/material';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import useAuthNavigation from '../useAuthNavigation';
import InvalidLink from './InvalidLink';
import { auth } from '../../../utils/firebase';
import { resetPassword } from '../../../app/store/slices/auth/authSlice';
import { getFirebaseErrorMessage } from '../firebase-error-codes/errorCodes';
import { H3, Body2, Body3, H4 } from '../../../themes/typography';
import '../../../styles/components/LoginForm.css';

export default function ResetPasswordForm() {
    const dispatch = useDispatch();
    const { backToLoginForm } = useAuthNavigation();
    const { oobCode } = useParams();
    const [isLoading, setIsLoading] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [isValidOobCode, setIsValidOobCode] = React.useState(false);
    const [isVerifying, setIsVerifying] = React.useState(true);
    const [email, setEmail] = React.useState('');

    // Get the action code from URL parameters
    const actionCode = oobCode;

    const formik = useFormik({
        initialValues: {
            newPassword: ''
        },
        validationSchema: Yup.object({
            newPassword: Yup.string().required('New password is required')
        }),
        onSubmit: async (values, { setSubmitting }) => {
            setIsLoading(true);

            try {
                await dispatch(
                    resetPassword({
                        email: email,
                        password: values.newPassword
                    })
                ).unwrap();

                setTimeout(() => {
                    setIsLoading(false);
                    backToLoginForm();
                }, 1000);
            } catch (error) {
                let errorMessage = getFirebaseErrorMessage(error);
                formik.setFieldError('newPassword', errorMessage);
            } finally {
                setIsLoading(false);
                setSubmitting(false);
            }
        }
    });

    // Verify the password reset code when the component mounts and set the email if the code is valid
    // This is done to ensure the user can reset their password without needing to enter their email again.
    React.useEffect(() => {
        const verifyResetCode = async () => {
            // if action code is not provided, show invalid link component
            if (!actionCode) {
                setIsVerifying(false);
                return;
            }

            setIsVerifying(true);
            try {
                const response = await verifyPasswordResetCode(auth, actionCode);
                setIsValidOobCode(true);
                setEmail(response); // Store the email from the response
                // console.log('Email from reset code:', response);
            } catch (error) {
                let errorMessage = getFirebaseErrorMessage(error);
                formik.setFieldError('newPassword', errorMessage);
                setIsValidOobCode(false);
            } finally {
                setIsVerifying(false);
            }
        };

        verifyResetCode();
    }, [actionCode]);

    return (
        <React.Fragment>
            <div style={resetPasswordFormStyles}>
                <Dialog open={true} sx={dialogStyles}>
                    {isVerifying ? (
                        <div
                            className="verification-loading-container"
                            style={loadingContainerStyle}
                        >
                            <CircularProgress size={40} sx={{ color: 'var(--secondary-color)' }} />
                            <H4 color="var(--secondary-color)">Verifying your reset link...</H4>
                        </div>
                    ) : isValidOobCode ? (
                        <div className="main-login-email-collect-container">
                            <div className="login-welcome-greetings">
                                <H3 color="var(--secondary-color)" fontWeight={700}>
                                    Create Password
                                </H3>
                                <Body2 color="var(--tertiary-color)">
                                    Enter a new password for your account
                                </Body2>
                            </div>

                            <form
                                className="main-login-form"
                                onSubmit={formik.handleSubmit}
                                autoComplete="off"
                            >
                                <div className="forgot-password-method-container slide-transition slide-active">
                                    <div className="main-switchable-login-form">
                                        <div className="switchable-login-input-container">
                                            <TextField
                                                fullWidth
                                                id="newPassword"
                                                name="newPassword"
                                                label="New Password"
                                                disabled={isLoading}
                                                type={showPassword ? 'text' : 'password'}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    edge="end"
                                                                    onClick={() =>
                                                                        setShowPassword(
                                                                            !showPassword
                                                                        )
                                                                    }
                                                                >
                                                                    {showPassword ? (
                                                                        <Visibility color="white" />
                                                                    ) : (
                                                                        <VisibilityOff color="white" />
                                                                    )}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }
                                                }}
                                                value={formik.values.newPassword}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={
                                                    formik.touched.newPassword &&
                                                    Boolean(formik.errors.newPassword)
                                                }
                                                helperText={
                                                    formik.touched.newPassword &&
                                                    formik.errors.newPassword
                                                }
                                            />
                                            <Stack sx={forgotStackStyle}>
                                                The new password must be at least 6 characters long
                                                and contain a mix of letters, numbers, and symbols.
                                            </Stack>
                                        </div>

                                        <div className="login-methods-container">
                                            <div className="login-methods-action-buttons">
                                                <Button
                                                    variant="main"
                                                    fullWidth
                                                    type="submit"
                                                    disabled={isLoading}
                                                >
                                                    Continue{' '}
                                                    {isLoading && (
                                                        <CircularProgress
                                                            size={20}
                                                            sx={{
                                                                marginLeft: '0.75rem',
                                                                color: 'var(--primary-color)'
                                                            }}
                                                        />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>

                            <div className="login-terms-agree-container">
                                <Body3>
                                    By continuing you accept Shmooz {''}
                                    <span className="terms">
                                        <Link>Terms of Service</Link>
                                    </span>{' '}
                                    and
                                    <span className="terms">
                                        {' '}
                                        {''}
                                        <Link>Privacy Policy</Link>
                                    </span>
                                </Body3>
                            </div>
                        </div>
                    ) : (
                        <InvalidLink onClick={backToLoginForm} />
                    )}
                </Dialog>
            </div>
        </React.Fragment>
    );
}

const dialogStyles = {
    '& .MuiPaper-root': {
        width: '475px',
        // height: '555px',
        padding: '2rem',
        display: 'flex',
        border: 'none',
        borderRadius: '1rem',
        background: 'var(--gradient-blue)',
        scrollbarWidth: 'none'
    }
};

const resetPasswordFormStyles = {
    backgroundColor: 'var(--primary-color)',
    height: '100vh',
    width: '100vw'
};

const forgotStackStyle = {
    color: 'var(--secondary-color)',
    background: 'var(--warning-light-bg-color)',
    padding: '0.875rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    alignSelf: 'stretch',
    textAlign: 'left',
    marginTop: '1rem'
};

const loadingContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '470px',
    gap: '0.5rem'
};
